import ko from "knockout";
import _ from "lodash";
import LOG from "services/loggingService";

/**
 * Base ViewModel class that includes mechanisms
 * for creating computed observables & subViews
 * that can easily be disposed of when the view model
 * is no longer needed.
 *
 * Because of the way ViewModel tracks computed values
 * and sub-views to be disposed of, it should not be used
 * as a class prototype. Prefer a non-prototypical
 * inheritance/extension strategy when creating specific
 * views.
 *
 * @exports viewModels/ViewModel
 */

export default abstract class ViewModel {
    private computedValues: ko.Computed<any>[] = [];
    private subscriptions: ko.Subscription[] = [];
    private subViews: ViewModel[] = [];

    // lifecycle
    onDispose?(...args: any[]): void;

    /**
     * Creates a new computed value that will be disposed
     * of when the dispose method is called on the view model.
     * Otherwise, this works like the standard ko.computed
     */
    computed = ((...args: Parameters<typeof ko.computed>) => {
        const computed = ko.computed(...args);
        this.computedValues.push(computed);
        return computed;
    }) as typeof ko.computed;

    pureComputed = ((...args: Parameters<typeof ko.pureComputed>) => {
        const pureComputed = ko.pureComputed(...args);
        this.computedValues.push(pureComputed);
        return pureComputed;
    }) as typeof ko.pureComputed;

    asyncComputed(callback: () => any, errorCallback: () => any, owner: any) {
        const asyncComputed = ko.asyncComputed(callback, errorCallback, owner);
        this.computedValues.push(asyncComputed);
        return asyncComputed;
    }

    subscribe<T>(
        observable: ko.Subscribable<T>,
        callback: (val: T) => void
    ): ko.Subscription {
        const subscription = observable.subscribe(callback);
        this.subscriptions.push(subscription);
        return subscription;
    }

    multiSubscribe(
        targets: ko.Subscribable<any>[],
        callback: (...args: any[]) => void
    ): ko.Subscription {
        const subscription = ko.multiSubscribe(
            targets as ko.Observable<any>[],
            callback
        );
        this.subscriptions.push(subscription);
        return subscription;
    }

    /**
     * Creates and returns a new view from the provided constructor.
     * Subsequent arguments are passed to the constructor when the
     * subView is created. The subView is also registered for cleanup
     * when dispose is called.
     */
    subView(SubViewConstructor: any, ...rest: any[]) {
        /*
         * Instantiates a new subView. The bind call above incorrectly binds 'this' to
         * subViewConstructor, but the use of 'new' below overrides that when calling
         * the proxy constructor.
         */
        const subView = new SubViewConstructor(...rest);
        this.subViews.push(subView);
        return subView;
    }

    /**
     * Informs the ViewModel that it's being disposed of
     * and that it should take any actions needed to clean
     * up. Specifically, it disposes of any computed
     * observables and calls onDispose if it has been
     * defined.
     */
    dispose(...args: any[]) {
        // Call dispose callbacks {
        if (this.onDispose) {
            this.onDispose(...args);
        }

        // Cleanup computed values
        this.computedValues.forEach((computedValue) => {
            computedValue.dispose();
        });
        this.computedValues = [];

        // Cleanup subscriptions
        this.subscriptions.forEach((subscription) => {
            subscription.dispose();
        });
        this.subscriptions = [];

        // Recurse to any associated view models
        this.subViews.forEach((subView) => {
            subView.dispose();
        });
        this.subViews = [];
    }

    /**
     * Disposes a specific subView and removes it from the
     * list of subviews.  This should be used if a subView
     * needs to be disposed of before the parent view itself
     * is disposed.
     */
    disposeSubView(subView: ViewModel) {
        const index = this.subViews.indexOf(subView);
        if (index == -1 || !_.isFunction(subView.dispose)) {
            LOG.warn(
                "Called disposeSubView with a bad argument; perhaps it was already disposed?"
            );
            return;
        }
        this.subViews.splice(index, 1);
        subView.dispose();
    }
}
